/**
 * The Cratus mark and lockup, for a build whose product is Cratus (`src/product/`).
 *
 * `BrandMark` and `BrandWordmark` dispatch here, so no call site knows which product it is
 * drawing — the header, the splash and the sign-in page stay exactly as they are on `dev`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS ARTWORK IS A RECONSTRUCTION, AND IT IS MEANT TO BE REPLACED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `cratus-mark.svg` was traced by eye from the "Cratus Original 3" frame of the Figma file
 * (CRATUS page, node 4305:10464) at 700%, because exporting the source vector needs an
 * editor's login. The wordmark beside it is TYPESET in Inter rather than drawn from the logo's
 * own letterforms. Both are close enough to read as Cratus and neither is the client's master
 * artwork: replace the SVG with the official export and the lettering with the exported
 * wordmark before anything here is shown to Cratus as final. docs/CRATUS.md §3 tracks it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY ONE COLOUR, WHATEVER THE SURFACE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Thunder's wordmark ships two inks because its letters are white or near-black. Cratus's logo
 * is red on both — mark and letters — and the red holds against either ground (5.2:1 on white,
 * and a logotype on the dark header is artwork, not body text). So `ink` is accepted for
 * parity with `BrandWordmark` and has nothing to switch.
 *
 * The red is the LOGO's and nothing else's. It is deliberately not `--brand` and not
 * `--danger`: the console's verdict colours are not re-hued per product (see
 * `src/product/profile.ts`), and borrowing the logo red for them would make a Cratus logo and a
 * critical alarm the same signal.
 */
import type { ReactNode } from 'react';
import markUrl from '@/assets/brand/cratus-mark.svg';

/** The logo red, sampled from the Figma frame. Artwork only — see the note above. */
const LOGO_RED = '#C23B3B';

/** `cratus-mark.svg`'s viewBox is 92 x 102; the mark is a little taller than it is wide. */
const MARK_ASPECT = 92 / 102;

export function CratusMark({ size }: { size: number }): ReactNode {
  return (
    <img
      src={markUrl}
      /* Height is the size; width follows the artwork, so the mark is never squashed square. */
      width={Math.round(size * MARK_ASPECT)}
      height={size}
      style={{ flex: 'none', display: 'block' }}
      alt="Cratus"
    />
  );
}

export function CratusLockup({ height }: { height: number }): ReactNode {
  return (
    /* One accessible name on the wrapper; the parts are decorative, as in `BrandWordmark`. */
    <span
      role="img"
      aria-label="Cratus"
      className="inline-flex shrink-0 items-center"
      style={{ height, gap: Math.round(height * 0.3) }}
    >
      <img
        src={markUrl}
        alt=""
        width={Math.round(height * MARK_ASPECT)}
        height={height}
        style={{ display: 'block' }}
      />
      <span
        aria-hidden
        style={{
          /* 0.6 of the mark, larger than the print lockup's ratio: at the header's 22px the
             print proportion puts the letters under 11px, which reads as a caption. */
          fontSize: Math.round(height * 0.6),
          fontWeight: 700,
          letterSpacing: '0.03em',
          lineHeight: 1,
          color: LOGO_RED,
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        CRATUS
      </span>
    </span>
  );
}
